import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Link } from 'react-router-dom';

export default function LifeInsurancePage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <span className="text-indigo-600">Life Insurance</span>
            </h1>
            <p className="text-xl text-gray-600">
              Child + Retirement + Savings + Investment in one plan
            </p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* What is Life Insurance */}
            <section className="bg-indigo-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What is Life Insurance?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Life insurance plans (also called traditional or endowment plans) combine life cover with a savings 
                component. Unlike term insurance which only pays on death, life insurance policies provide 
                <strong> maturity benefits</strong> if you survive the policy term.
              </p>
              <p className="text-gray-700 leading-relaxed">
                These plans are ideal for disciplined savings while ensuring your family is protected. They offer 
                guaranteed returns and help achieve long-term financial goals.
              </p>
            </section>

            {/* Types of Life Insurance */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Types of Life Insurance Plans</h2>
              
              {/* Featured Priority Plans */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="bg-yellow-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-full">⭐ PRIORITY OFFERINGS</span>
                  <span className="text-sm text-gray-600">Recommended by our advisors</span>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  <div className="border-2 border-rose-200 rounded-xl p-6 hover:border-rose-400 transition-colors">
                    <h3 className="font-bold text-lg text-rose-600 mb-2">Retirement Plans</h3>
                    <p className="text-gray-600 mb-3">
                      Build retirement corpus with regular pension payouts post-retirement. Deferred annuity options available.
                    </p>
                    <div className="text-sm text-gray-500">
                      <strong>Returns:</strong> 5-7% | <strong>Suitable for:</strong> Retirement planning, pension needs, senior citizens
                    </div>
                  </div>

                  <div className="border-2 border-cyan-200 rounded-xl p-6 hover:border-cyan-400 transition-colors">
                    <h3 className="font-bold text-lg text-cyan-600 mb-2">Child Plans</h3>
                    <p className="text-gray-600 mb-3">
                      Secure your child's education and marriage expenses. Waiver of premium on parent's death.
                    </p>
                    <div className="text-sm text-gray-500">
                      <strong>Returns:</strong> 5-10% | <strong>Suitable for:</strong> Child's education, marriage planning, financial security
                    </div>
                  </div>

                  <div className="border-4 border-yellow-400 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-bold text-xl text-orange-700 mb-2">Savings Plans</h3>
                      <span className="bg-yellow-400 text-gray-900 text-xs font-bold px-2 py-1 rounded">FEATURED</span>
                    </div>
                    <p className="text-gray-700 mb-3 font-medium">
                      Best for disciplined savings with life cover. Guaranteed returns with safety and liquidity.
                    </p>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div><strong>Returns:</strong> 5-10% (guaranteed)</div>
                      <div><strong>Features:</strong> Bonus accumulation, loan facility, tax benefits</div>
                      <div><strong>Suitable for:</strong> Risk-averse savers, first-time investors, emergency fund building</div>
                    </div>
                  </div>

                  <div className="border-4 border-yellow-400 bg-gradient-to-br from-yellow-50 to-green-50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-bold text-xl text-green-700 mb-2">Wealth Plans</h3>
                      <span className="bg-yellow-400 text-gray-900 text-xs font-bold px-2 py-1 rounded">FEATURED</span>
                    </div>
                    <p className="text-gray-700 mb-3 font-medium">
                      Maximize wealth creation with market-linked growth potential and life protection.
                    </p>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div><strong>Returns:</strong> 9-15% (market-linked)</div>
                      <div><strong>Features:</strong> Fund switching, top-up options, partial withdrawals</div>
                      <div><strong>Suitable for:</strong> Long-term wealth building, HNI clients, aggressive growth seekers</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Standard Plans */}
              <div className="space-y-4">
                <div className="border-2 border-indigo-200 rounded-xl p-6 hover:border-indigo-400 transition-colors">
                  <h3 className="font-bold text-lg text-indigo-600 mb-2">Endowment Plans</h3>
                  <p className="text-gray-600 mb-3">
                    Pay premiums for 10-20 years. Get sum assured + bonuses on maturity or death.
                  </p>
                  <div className="text-sm text-gray-500">
                    <strong>Returns:</strong> 5-6% (guaranteed) | <strong>Suitable for:</strong> Conservative investors, child education
                  </div>
                </div>
                
                <div className="border-2 border-purple-200 rounded-xl p-6 hover:border-purple-400 transition-colors">
                  <h3 className="font-bold text-lg text-purple-600 mb-2">Money Back Plans</h3>
                  <p className="text-gray-600 mb-3">
                    Get periodic payouts (20-25% of sum assured) during policy term, remaining on maturity.
                  </p>
                  <div className="text-sm text-gray-500">
                    <strong>Returns:</strong> 5-6% | <strong>Suitable for:</strong> Those needing regular income, child milestones
                  </div>
                </div>

                <div className="border-2 border-blue-200 rounded-xl p-6 hover:border-blue-400 transition-colors">
                  <h3 className="font-bold text-lg text-blue-600 mb-2">ULIP (Unit Linked Insurance Plan)</h3>
                  <p className="text-gray-600 mb-3">
                    Part of premium goes to life cover, rest invested in equity/debt funds. Market-linked returns.
                  </p>
                  <div className="text-sm text-gray-500">
                    <strong>Returns:</strong> 8-12% (market-linked) | <strong>Suitable for:</strong> Long-term wealth creation (10+ years)
                  </div>
                </div>

                <div className="border-2 border-green-200 rounded-xl p-6 hover:border-green-400 transition-colors">
                  <h3 className="font-bold text-lg text-green-600 mb-2">Whole Life Plans</h3>
                  <p className="text-gray-600 mb-3">
                    Coverage till age 100. Guaranteed maturity payout plus life cover.
                  </p>
                  <div className="text-sm text-gray-500">
                    <strong>Returns:</strong> 5-6% | <strong>Suitable for:</strong> Estate planning, long-term family security
                  </div>
                </div>
              </div>
            </section>

            {/* Benefits */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Benefits</h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Dual Benefit:</strong>
                    <span className="text-gray-700"> Life protection + Savings/Investment in one plan</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Guaranteed Returns:</strong>
                    <span className="text-gray-700"> Traditional plans offer assured maturity benefits (5-6% returns)</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Forced Savings:</strong>
                    <span className="text-gray-700"> Builds financial discipline through regular premium payments</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Tax Benefits:</strong>
                    <span className="text-gray-700"> Premiums deductible under 80C, maturity proceeds tax-free under 10(10D)</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Loan Facility:</strong>
                    <span className="text-gray-700"> Take loan against policy surrender value in emergencies</span>
                  </div>
                </li>
              </ul>
            </section>

            {/* Life Insurance vs Term Insurance */}
            <section className="bg-gradient-to-br from-indigo-600 to-purple-600 text-white rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6">Life Insurance vs Term Insurance</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white text-gray-900 rounded-xl p-6">
                  <h3 className="font-bold text-lg text-indigo-600 mb-3">Life Insurance</h3>
                  <ul className="space-y-2 text-sm">
                    <li>✓ Maturity benefit + Death benefit</li>
                    <li>✓ Guaranteed returns (5-6%)</li>
                    <li>✓ Savings component</li>
                    <li>✓ Higher premiums</li>
                    <li>✓ Good for disciplined saving</li>
                  </ul>
                </div>
                <div className="bg-white text-gray-900 rounded-xl p-6">
                  <h3 className="font-bold text-lg text-blue-600 mb-3">Term Insurance</h3>
                  <ul className="space-y-2 text-sm">
                    <li>✓ Only death benefit</li>
                    <li>✓ No maturity benefit</li>
                    <li>✓ Pure protection</li>
                    <li>✓ Very low premiums</li>
                    <li>✓ Maximum coverage</li>
                  </ul>
                </div>
              </div>
              <p className="text-indigo-100 text-sm mt-4 text-center">
                💡 <strong>Expert Tip:</strong> Buy term insurance for protection + invest separately in mutual funds for better returns
              </p>
            </section>

            {/* Who Should Buy */}
            <section className="bg-green-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Who Should Buy Life Insurance?</h2>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Those who lack investment discipline and need forced savings</span>
                </li>
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Conservative investors seeking guaranteed returns</span>
                </li>
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Parents planning for child's education/marriage</span>
                </li>
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Those wanting life cover + retirement corpus together</span>
                </li>
              </ul>
            </section>

            {/* Licensed Advisor Badge */}
            <section className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-2xl p-8">
              <div className="flex items-start gap-6">
                <div className="bg-blue-600 rounded-xl p-4 flex-shrink-0">
                  <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Licensed Life Insurance Advisor</h3>
                  <p className="text-gray-700 mb-4">
                    K Satish is officially licensed with SBI Life Insurance Company Ltd, ensuring you receive 
                    professional and compliant life insurance advice to protect your family's future.
                  </p>
                  <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-600 mb-1 font-semibold">Insurance Company:</p>
                        <p className="text-sm font-bold text-blue-800">SBI Life Insurance Company Ltd</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1 font-semibold">License Code:</p>
                        <p className="text-lg font-mono font-bold text-blue-700">SLI991485554</p>
                      </div>
                    </div>
                    <a
                      href="https://sateeshkotta991485554.lifemitra.com/product-detail/smart-scholar-plus_9c89fb3b-827f-4df1-bdba-b4a6c51f2433.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-all font-semibold text-sm"
                    >
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      View My Authorized Products
                    </a>
                    <p className="text-xs text-gray-500 text-center mt-2">Click to verify my credentials and view available insurance plans</p>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="text-center bg-indigo-600 text-white rounded-2xl p-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Find the Right Life Insurance Plan
              </h2>
              <p className="text-indigo-100 mb-6">
                Get expert advice from K Satish on choosing between different life insurance options
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/#contact"
                  className="inline-block bg-white text-indigo-600 px-8 py-4 rounded-xl hover:bg-gray-50 transition-all shadow-lg font-semibold"
                >
                  Request Consultation
                </Link>
                <a
                  href="https://wa.me/919248852187?text=Hi, I want to know more about Life Insurance plans"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-green-500 text-white px-8 py-4 rounded-xl hover:bg-green-600 transition-all shadow-lg font-semibold"
                >
                  WhatsApp Now
                </a>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
